// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartActions - common actions for all report part
 */
import React from 'react';
import { toJS } from 'mobx';
import moment from 'moment';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ReportPartCommonActionPopup from './Izenda.ReportPartCommonActionPopup';
import reportDesignerAction from 'modules/ReportDesigner/Fields/ReportBody/Izenda.ReportPartFrontSideAction';
import reportPartFrontSideViewerAction from 'modules/ReportDesigner/Fields/ReportBody/ReportPartFrontSideViewer/Izenda.ReportPartFrontSideViewerAction';
import dashboardAction from 'modules/Dashboard/DashboardDesigner/DashboardGrid/DashboardTile/ReportPartTile/Izenda.DashboardTileAction';
import connect from 'share/enhancers/Izenda.Connect';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import { compose, withHandlers, setPropTypes, lifecycle } from 'recompose';
import {find, filter, get, flow, invoke,curry, memoize, toNumber, toString} from 'lodash/fp';

const refreshData = (props: any, isRefreshData: boolean) => {
  const reportPart = props.reportPart || {};
  const reportPartContent = reportPart.reportPartContent;

  if (!isRefreshData) {
    reportPart.clearRefreshData && reportPart.clearRefreshData();
  } else {
    reportPart.refreshData &&
      reportPart.refreshData(reportPartContent.dataRefreshInterval, numRecords => {
        props.needUpdatePreview(
          reportPart,
          reportPartContent.getDrilldownInfo && reportPartContent.getDrilldownInfo(),
          numRecords,
          true
        );
      });
  }
};

const pointAction = (props: any, fieldConfig: any, filters: Array<any>, separator: any) => {
  // find exactly record data matched with value clicked
  const previewData = toJS(props.reportPart.previewData);
  const previewFieldsMapping = previewData.fieldsMapping;
  const records = previewData.records;
  let foundRecords = [];

  filters.forEach(filterItem => {
    const field = find({ fieldNameAlias: filterItem.key }, previewFieldsMapping);
    foundRecords = foundRecords.concat(
      filter(record => {
        const columnName = get('columnName', field);
        let fieldValue = flow(get(columnName), toString)(record);

        const filterValue = flow(get('value'), toString)(filterItem);

        const isDateTimeType = get('fieldElement.isDateTimeType', fieldConfig);
        let dateTimeFilterValue = null;

        // For date time type : filter value will be formatted value or timespan
        // Need to convert timespan to correct format to compare
        if (isDateTimeType) {
          fieldValue = convertToDateTimeFormat(fieldConfig.fieldFormatData, fieldValue);
          dateTimeFilterValue = convertToDateTimeFormat(fieldConfig.fieldFormatData, moment(toNumber(filterValue)));
        }
        return fieldValue === filterValue || fieldValue === dateTimeFilterValue;
      }, records)
    );
  });

  // find record by separator value
  if (separator) {
    const separatorColumnName = separator.separatorColumnName;
    const separatorValue = separator.separatorValue;

    foundRecords = foundRecords.filter(record => record[separatorColumnName] === separatorValue);
  }
  ReportPartUtils.openHyperlinkAction(props, fieldConfig, foundRecords, ReportPartCommonActionPopup);
};

const invokeAfterRenderCallBack = function() {
  invoke('afterRenderCallBack', this.props);
};

const frontSideActions = connect(
  setPropTypes({
    reportPart: React.PropTypes.object.isRequired,
    needUpdatePreview: React.PropTypes.func.isRequired,
    getDrillDownRecords: React.PropTypes.func.isRequired,
    afterRenderCallBack: React.PropTypes.func
  }),
  lifecycle({
    componentDidUpdate: invokeAfterRenderCallBack,
    componentDidMount: invokeAfterRenderCallBack
  }),
  withHandlers({
    refreshData: curry(refreshData),
    pointAction: curry(pointAction)
  })
);

const composeActionDashboard = memoize(compose(dashboardAction, frontSideActions));
const composeActionReportPartViewer = memoize(compose(reportPartFrontSideViewerAction, frontSideActions));
const composeActionReport = memoize(compose(reportDesignerAction, frontSideActions));

export default {
  dashboard: composeActionDashboard,
  viewer: composeActionReportPartViewer,
  report: composeActionReport
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/Izenda.ReportPartActions.js